import React from "react";

const ikigaiWeeks = [
  {
    emoji: "🔥",
    title: "IGNITE",
    phase: "DISCOVER",
    color: "from-orange-500 to-red-500",
    objective: "Discover what energizes you.",
    outcome: "⚡ Personal Energy Map",
  },
  {
    emoji: "⚡",
    title: "AWAKEN",
    phase: "DISCOVER",
    color: "from-yellow-500 to-orange-500",
    objective: "Identify frustrations worth solving.",
    outcome: "📝 Problem Awareness Map",
  },
  {
    emoji: "💪",
    title: "UNLOCK",
    phase: "DISCOVER",
    color: "from-green-500 to-emerald-500",
    objective: "Discover hidden strengths.",
    outcome: "🏆 Strength Inventory",
  },
  {
    emoji: "🔭",
    title: "EXPLORE",
    phase: "DISCOVER",
    color: "from-cyan-500 to-blue-500",
    objective: "Scan opportunities around you.",
    outcome: "🚀 Opportunity Map",
  },
  {
    emoji: "❤️",
    title: "IMPACT",
    phase: "DISCOVER",
    color: "from-pink-500 to-rose-500",
    objective: "Define your contribution.",
    outcome: "🤝 Impact Statement",
  },
  {
    emoji: "🎯",
    title: "DECLARE",
    phase: "DISCOVER",
    color: "from-purple-500 to-fuchsia-500",
    objective: "Create your 90-Day IKIGAI.",
    outcome: "📌 Purpose Statement",
  },
  {
    emoji: "📚",
    title: "LEARN",
    phase: "BUILD",
    color: "from-blue-500 to-indigo-500",
    objective: "Acquire essential knowledge.",
    outcome: "🎓 Learning Blueprint",
  },
  {
    emoji: "🛠️",
    title: "CREATE",
    phase: "BUILD",
    color: "from-green-500 to-teal-500",
    objective: "Build something valuable.",
    outcome: "🏗️ Creation Plan",
  },
  {
    emoji: "🌎",
    title: "SERVE",
    phase: "BUILD",
    color: "from-cyan-500 to-sky-500",
    objective: "Help people around you.",
    outcome: "💝 Contribution Targets",
  },
  {
    emoji: "📈",
    title: "EXECUTE",
    phase: "BUILD",
    color: "from-orange-500 to-amber-500",
    objective: "Measure progress and momentum.",
    outcome: "📊 Personal KPI Dashboard",
  },
  {
    emoji: "🧠",
    title: "INTEGRATE",
    phase: "INTEGRATE",
    color: "from-purple-500 to-indigo-500",
    objective: "Capture lessons and wisdom.",
    outcome: "📖 Wisdom Journal",
  },
  {
    emoji: "🦋",
    title: "RENEW",
    phase: "RENEW",
    color: "from-emerald-500 to-green-500",
    objective: "Design your next evolution.",
    outcome: "✨ Next IKIGAI Statement",
  },
];

const phaseColors = {
  DISCOVER: "border-yellow-500 bg-yellow-500/10",
  BUILD: "border-cyan-500 bg-cyan-500/10",
  INTEGRATE: "border-purple-500 bg-purple-500/10",
  RENEW: "border-green-500 bg-green-500/10",
};

const PDIKIGAI = () => {
  return (
    <div className="space-y-2 bg-gray-900 p-4 rounded-xl text-white border-t-2 border-cyan-200 mt-1">
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-950 via-indigo-950 to-black p-2 lg:p-4">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_#9333ea,_transparent_40%)]"></div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-700 bg-purple-900/40 text-xs font-semibold mb-1">
            🦋 IKIGAI BOS (Behavioral Operating System)
          </div>
          <h2 className="text-xs lg:text-sm text-purple-300 mt-1 font-bold">
            Purpose → Action → Wisdom → Renewal
          </h2>

          <p className="max-w-4xl mt-2 text-gray-300 text-xs lg:text-sm leading-relaxed">
            Discover your purpose. Build momentum. Capture wisdom. Reinvent
            yourself every 90 days.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-1">
            <div className="bg-black/30 border border-yellow-700 rounded-xl p-2">
              <div className="text-sm lg:text-lg">🔍</div>
              <div className="font-bold text-xs lg:text-sm mt-1">DISCOVER</div>
              <div className="text-xs text-gray-400">Weeks 1 - 6</div>
            </div>

            <div className="bg-black/30 border border-cyan-700 rounded-xl p-2">
              <div className="text-sm lg:text-lg">🚀</div>
              <div className="font-bold text-xs lg:text-sm mt-1">BUILD</div>
              <div className="text-xs text-gray-400">Weeks 7 - 10</div>
            </div>

            <div className="bg-black/30 border border-purple-700 rounded-xl p-2">
              <div className="text-sm lg:text-lg">🧠</div>
              <div className="font-bold text-xs lg:text-sm mt-1">INTEGRATE</div>
              <div className="text-xs text-gray-400">Week 11</div>
            </div>

            <div className="bg-black/30 border border-green-700 rounded-xl p-2">
              <div className="text-sm lg:text-lg">🦋</div>
              <div className="font-bold text-xs lg:text-sm mt-1">RENEW</div>
              <div className="text-xs text-gray-400">Week 12</div>
            </div>
          </div>
        </div>
      </div>

      {/* TIMELINE */}

      <div className="px-2 py-1">
        <div className="text-center mb-4">
          <h2 className="text-sm lg:text-lg font-black text-cyan-300">
            🌟 12 Week Evolution Journey
          </h2>
          <p className="text-gray-400 mt-1">
            Every 90 Days. A Better Version Of You.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {ikigaiWeeks.map((week, index) => (
            <div
              key={index}
              className={`
            rounded-xl
            border
            ${phaseColors[week.phase]}
            backdrop-blur-md
            overflow-hidden
            hover:scale-[1.02]
            transition-all
            duration-300
          `}
            >
              <div className={`bg-gradient-to-r ${week.color} px-2 py-1`}>
                <div className="flex justify-between items-center">
                  <div className="text-xs lg:text-sm">{week.emoji}</div>

                  <div className="text-xs lg:text-sm font-bold bg-black/30 px-2 py-1 rounded-full">
                    WEEK {index + 1}
                  </div>
                </div>

                <h3 className="text-xs lg:text-sm font-black mt-1">{week.title}</h3>
              </div>

              <div className="px-2 py-1">
                <div className="text-xs lg:text-sm text-cyan-300 font-bold mb-1">
                  {week.phase}
                </div>

                <p className="text-[10px] text-xs text-gray-300 mb-1">{week.objective}</p>

                <div className="bg-black/20 rounded-xl px-2 py-1 border border-white/10">
                  <div className="text-[10px] text-xs text-gray-400 mb-1">OUTCOME</div>

                  <div className="text-[10px] text-xs font-semibold text-green-300">
                    {week.outcome}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TRANSFORMATION */}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 px-2 pb-2">
        <div className="bg-red-950/20 border border-red-800 rounded-xl p-4">
          <h3 className="text-xs lg:text-sm font-black text-red-300 mb-1">🌱 BEFORE</h3>

          <div className="space-y-3 text-gray-300">
            <div className="text-[11px] lg:text-xs">😴 Low Energy</div>
            <div className="text-[11px] lg:text-xs">🤷 Lack Of Direction</div>
            <div className="text-[11px] lg:text-xs">📱 Constant Distraction</div>
            <div className="text-[11px] lg:text-xs">😵 Inconsistent Habits</div>
            <div className="text-[11px] lg:text-xs">❓ Unclear Purpose</div>
            <div className="text-[11px] lg:text-xs">📉 Limited Progress</div>
          </div>
        </div>

        <div className="bg-green-950/20 border border-green-800 rounded-xl p-4">
          <h3 className="text-xs lg:text-smfont-black text-green-300 mb-1">🦅 AFTER</h3>

          <div className="space-y-3 text-gray-300">
            <div className="text-[11px] lg:text-xs">🎯 Clear Purpose</div>
            <div className="text-[11px] lg:text-xs">⚡ High Energy</div>
            <div className="text-[11px] lg:text-xs">📈 Consistent Progress</div>
            <div className="text-[11px] lg:text-xs">💪 Strong Habits</div>
            <div className="text-[11px] lg:text-xs">🌎 Meaningful Contribution</div>
            <div className="text-[11px] lg:text-xs">🚀 Next Level Growth</div>
          </div>
        </div>
      </div>

      {/* PHILOSOPHY */}

      <div className="px-4 pb-2">
        <div className="rounded-xl border border-cyan-700 bg-gradient-to-r from-cyan-950 via-blue-950 to-indigo-950 p-4">
          <h2 className="text-sm lg:text-lg text-cyan-200 text-center mb-1">
            🚪 The PurpleDoor Difference
          </h2>

          <div className="max-w-5xl mx-auto text-center">
            <p className="text-xs lg:text-sm text-gray-200 leading-relaxed">
              Your life does not need one IKIGAI.
            </p>

            <p className="text-xs lg:text-sm text-gray-200 leading-relaxed mt-1">
              🌱 Discover An IKIGAI
            </p>

            <p className="text-xs lg:text-sm text-gray-200 leading-relaxed">
              🚀 Live It For 90 Days
            </p>

            <p className="text-xs lg:text-sm text-gray-200 leading-relaxed">
              🧠 Learn From It
            </p>

            <p className="text-xs lg:text-sm text-gray-200 leading-relaxed">
              🦋 Evolve Into The Next One
            </p>

            <div className="my-2 text-xs lg:text-sm">⬇️</div>

            <h3 className="text-xs lg:text-sm font-black text-purple-300">
              Repeat.
            </h3>

            <p className="mt-2 text-xs lg:text-sm text-gray-300 max-w-3xl mx-auto">
              Growth is not a destination. Growth is a cycle.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}

      <div className="bg-gradient-to-r from-purple-900 via-fuchsia-900 to-purple-950 p-4 text-center">
        <div className="text-xs lg:text-sm mb-1">🚪</div>

        <h2 className="text-xs lg:text-sm text-cyan-200 mb-1">
          PurpleDoor IKIGAI BOS
        </h2>

        <p className="text-xs lg:text-sm text-purple-200">
          Purpose → Action → Wisdom → Renewal
        </p>

        <p className="mt-2 text-xs lg:text-sm text-gray-300 max-w-4xl mx-auto">
          Every 90 days, discover a new purpose. Every purpose creates new
          actions. Every action creates wisdom. Every wisdom creates a better
          version of you.
        </p>
      </div>
    </div>
  );
};

export default PDIKIGAI;
